
/**
 * The AuthProvider supports using SSR
 */

import { h, ComponentChildren, createContext } from 'preact'; /** @jsx h */
import { useContext, useState, useEffect } from 'preact/hooks';
import { useLocation } from 'preact-iso';

import { Session } from '@pyqml/model';
import axios from 'axios';

import { useEnvironment } from './Environment';

import murmurHash3 from 'murmurhash3js';
const createHash = murmurHash3.x86.hash128;

export type IAuthProviderProps = {
    children?: ComponentChildren;
    
    authKey: string;

    loginRoute: string;

    loginEndpoint: string;

    signupEndpoint: string;

    /**
     * where to send users who singed up / signed in
     */
    signinDestination: string;

};


export type IAuthenticatedPromise<T> = {
    method: string;

    endpoint: string;

    queryData?: T;

    apiIdentifier?: string;
    contentType?: string;

    onEnd?: ((success: boolean, responseData: any) => void);
}





export type IAuth<T> = IAuthenticatedPromise<T> & {
    manual?: boolean;

    onLoad?: any | undefined;

    reloadOn?: Array<any> | undefined;

}

/**
 * The context for the AuthProvider
 */
export type IAuthProvider = {
    token?: string;
    subscriptionLevel?: Session.SubscriptionLevel;
    subscriptionDuration?: Session.SubscriptionDuration;
    nextDue?: string;
    subscriptionState?: Session.SubscriptionState;
    onSignUp?: any; //handleEvent(props.signupEndpoint),
    onLogin?: any; //handleEvent(props.loginEndpoint),
    onLogout?: any; // handleLogout,
    authQuery?: (args: IAuth<any>) => any;
    authPromise?: (args: IAuthenticatedPromise<any>) => Promise<any>;
    refetchAuth?: () => void;
}

const AuthProviderContext = createContext<IAuthProvider>({});


export const useAuth = () => {
    return useContext(AuthProviderContext);
};



const useLocalStorage = (storageKey, fallbackState) => {
    // checking the type of localStorage doesn't work
    // therefore, we handle the exception
    const storedValue = (function () {
        try {
            return localStorage.getItem(storageKey);
        } catch {
            return undefined;
        }
    })();

    const [value, setValue] = useState(
        storedValue
            ? (() => {
                  try {
                      /** if it is an object */
                      return JSON.parse(storedValue);
                  } catch (error) {
                      /** if it is a string */
                      return storedValue;
                  }
                  /** if not stored */
              })()
            : fallbackState
    );

    useEffect(() => {
        if (value) {
            localStorage.setItem(storageKey, JSON.stringify(value));
        } else {
            localStorage.removeItem(storageKey);
        }
    }, [value, storageKey]);

    return [value, setValue];
};


export const AuthProvider = (props: IAuthProviderProps, hash?: string) => {
    const environment = useEnvironment();
    const location = useLocation();

    const [token, setToken] = useLocalStorage(`token-${props.authKey}`, undefined);

    const [subscriptionLevel, setSubscriptionLevel] = useLocalStorage(`level-${props.authKey}`, undefined);
    const [subscriptionDuration, setSubscriptionDuration] = useLocalStorage(`duration-${props.authKey}`, undefined);
    const [nextDue, setNextDue] = useLocalStorage(`nextdue-${props.authKey}`, undefined);
    const [subscriptionState, setSubscriptionState] = useLocalStorage(`state-${props.authKey}`, undefined);
    
    
    useEffect(() => {
        if (token) {
            localStorage.setItem(props.authKey, token);
        }
    }, [token]);

    const handleLogout = () => {
        setToken(null);
        setSubscriptionLevel(undefined);
        setSubscriptionDuration(undefined);
        setNextDue(undefined);
        setSubscriptionState(undefined);
    };

    /**
     * Handles signup and signIn events
     * @param endpoint
     * @returns
     */
    const handleEvent = (endpoint: string) => async (user: any, onEnd?: (success: boolean, responseData: any) => boolean) => {
        axios
            .post(`${environment.dataUrl}${endpoint}`, {
                headers: {
                    'Content-Type': 'application/json'
                },
                data: user
            })
            .then(async (response) => {
                if (response?.data?.token !== undefined) {
                    console.log("WRITING response data: ", response.data)
                    setToken(response.data.token);
                    setSubscriptionLevel(response.data.subscriptionLevel);
                    setSubscriptionDuration(response.data.subscriptionDuration);
                    setNextDue(response.data.nextDue);
                    setSubscriptionState(response.data.subscriptionState);

                    if(onEnd?.(true, response?.data) ?? true) {
                        const origin = (function () {
                           // if (location.state?.from?.pathname !== undefined) return location.state.from.pathname;
                            if (props.signinDestination?.length > 0) return props.signinDestination;
                            return '/';
                        })();
                        location.route(origin);
                    }
                    
                } else {
                    onEnd?.(true, response?.data);
                }
            })
            .catch((err) => {
                if (err?.response?.status === 498) {
                    setToken(null);
                    setSubscriptionLevel(undefined);
                    setSubscriptionDuration(undefined);
                    setNextDue(undefined);
                    setSubscriptionState(undefined);

                    if (onEnd?.(false, undefined) ?? true) {
                        location.route(props.loginRoute);
                    }
                    
                } else if (onEnd?.(false, undefined) ?? true) {
                    location.route(props.loginRoute);
                }
            });
    };


    


    function authenticatedPromise<T>(args: IAuthenticatedPromise<T>) {
        
        return new Promise((resolve, reject) => {
            axios({
                url: `${
                    args.apiIdentifier && environment[args.apiIdentifier]
                        ? environment[args.apiIdentifier]
                        : environment.dataUrl
                }${args.endpoint}`,
                method: args.method,
                headers: {
                    'Content-Type': args.contentType ? args.contentType : 'application/json',
                    authorization: token,
                    subdomain: environment.subdomain
                },
                data: args.queryData
            })
            .then((result) => {
                args.onEnd?.(true, result?.data)
                resolve(result)
            })
            .catch((err) => {
                if (err?.response?.status === 498) {
                    setToken(null);
                    setSubscriptionLevel(undefined);
                    setSubscriptionDuration(undefined);
                    setNextDue(undefined);
                    setSubscriptionState(undefined);

                    args.onEnd?.(false, undefined)
                    //location.route(props.loginRoute);
                } else {
                    console.error(err);
                    args.onEnd?.(false, undefined)
                    resolve(err);
                }
            });
        });
    }

    

    function authenticatedQuery<T>(args: IAuth<T>) {
        // const authenticatedQueryHook = (method: string, endpoint: string, queryData = {}, manual = false, onLoad = undefined, usePyapi: boolean = false, refetchOn = []) => {
        const [result, setResult] = useState<any>({
            data: undefined,
            loading: undefined,
            error: undefined
        });
        const [cnt, setCnt] = useState(0);

        useEffect(
            () => {
                const fetchData = async () => {
                    if ((args.manual && cnt === 0) || result.loading) {
                        return;
                    }
                    setResult({
                        data: undefined,
                        loading: true,
                        error: false
                    });

                    authenticatedPromise(args)
                    .then((response: any) => {
                        if (args.onLoad !== undefined) {
                            args.onLoad?.(response);
                        }
                        setResult({
                            data: response?.data,
                            loading: false,
                            error: false
                        });
                    })
                    .catch((err) => {
                        // console.log(err)
                        if (err?.response?.status === 498) {
                            setToken(null);
                            setSubscriptionLevel(undefined);
                            setSubscriptionDuration(undefined);
                            setNextDue(undefined);
                            setSubscriptionState(undefined);

                        } else {
                            setResult({
                                data: undefined,
                                loading: false,
                                error: err
                            });
                        }
                    });
                };
                fetchData();
            },
            [cnt].concat(args?.reloadOn ? args?.reloadOn : [])
        );

        return [result, () => setCnt(cnt + 1), cnt, (data) => setResult({data: data, loading: false, error: false})];
    }



    return (
        <AuthProviderContext.Provider value={{
            token,
            subscriptionLevel,
            subscriptionDuration,
            nextDue,
            subscriptionState,
            refetchAuth: () => handleEvent(props.loginEndpoint)({authHeader: token}),
            onSignUp: handleEvent(props.signupEndpoint),
            onLogin: handleEvent(props.loginEndpoint),
            onLogout: handleLogout,
            authQuery: authenticatedQuery,
            authPromise: authenticatedPromise
        }}>
            {props.children}
        </AuthProviderContext.Provider>
    );

}
